import {Router} from '@angular/router';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import {CDSNode, CDSSystem, SearchAllSettingsDisplay} from '../services/capd.model';
import { OauthService } from '../services/oauth.service';
import { CDSTreeService } from '../services/cds.service';
import {APIURL} from "../services/url.config";
import {ExportToCsv} from "export-to-csv";
import {document} from "ngx-bootstrap/utils";
import { OrgService } from '../services/org.service';
import {AlertService} from "../services/alert.service";

import {ConfirmationService, MenuItem} from 'primeng/api';
import {DEFAULT_INTERRUPTSOURCES, Idle} from "@ng-idle/core";
import {Keepalive} from "@ng-idle/keepalive";
import {Subscription} from "rxjs";
import {AuditService} from '../services/audit.service';
import { SecureTokenService } from '../services/secure-token.service';
export let ROOT_ORGS: string[];

@Component({
  selector: 'capd-admin',
  templateUrl: './capd-admin.component.html',
  styleUrls: ['./capd-admin.component.less'],
  providers: [OrgService, AuditService]
})

/**
 * The splash page of the Admin app.  This probably won't need much touching at this point.  It
 * mostly routes the user to the correct page-- login or main-- depending on whether the user
 * currently has a valid token.
 */
export class CAPDAdminComponent implements OnInit, OnDestroy {

  username: string;
  root: CDSNode;
  display: string[];
  applicationVersion: string;
  environmentName: string = '';
  showCustom = false;
  uiVersion: string = 'Classic';

  // Audit Logging Parameters
  startDate: string;
  endDate: string;
  inputStartDate: string;
  inputEndDate: string;
  maxDate: string;
  auditLogs: any[];
  unfilteredLogs: any[];
  operationList: string[] = ['create', 'update', 'upsert', 'delete'];
  operationTypeList: string[] = ['Mappings', 'Packages', 'Rules', 'Settings'];
  auditLogsRequested: boolean = false;
  loadingLogs: boolean = false;
  loadingSearch: boolean = false;
  displayFilters: boolean = false;
  pageNumber: number = 0;
  take: number = 100;
  sortType: string = 'sortTypeDesc';

  currOperation: string = 'Select';
  searchOperationType: string = 'Select';
  searchUser: string = 'Select';
  searchOrg: string = 'Select';

  userList: any = [];
  orgList: any = [];

  // search all settings
  searchAllSettingsSearchText: string = '';
  searchAllSettingsSearchType: string = '';
  searchAllSettingsResult: Array<SearchAllSettingsDisplay>;
  invalidSearchAllSettingsResult: Array<SearchAllSettingsDisplay>;
  invalidResultsTooltip: string = 'Click to export results.';

  oidToFind: string;
  oidError: boolean = false;
  oidErrorText: string = 'Incorrect oid format';
  oidName: string;
  loadingOidName: boolean = false;

  menuItems: MenuItem[];

  noResults = false;
  versionSwitchEnabled = true;

  @Output() uiChangeEvent = new EventEmitter<string>();

  @ViewChild('aboutModal') aboutModal: ElementRef;
  @ViewChild('searchAllSettingsModal') searchAllSettingsModal: ElementRef;
  @ViewChild('findOrgByOidModal') findOrgByOidModal: ElementRef;
  @ViewChild('auditLogModal') auditLogModal: ElementRef;
  @ViewChild('auditLogV2Modal') auditLogv2Modal: ElementRef;
  @ViewChild('clientDash') clientDash: ElementRef;
  @ViewChild('faq') faq: ElementRef;
  @ViewChild('getAuditLogsButton') getAuditLogsButton: ElementRef;

  getLogsForm = new UntypedFormGroup({
    startDate: new UntypedFormControl(null, [Validators.required]),
    endDate: new UntypedFormControl(null, [Validators.required]),
    dateDropdown: new UntypedFormControl("Select Date Range", [Validators.required]),
    filterOperation: new UntypedFormControl("Select"),
    filterOperationType: new UntypedFormControl("Select"),
    filterUser: new UntypedFormControl("Select"),
    filterOrg: new UntypedFormControl("Select")
  });

  getLogsFormV2 = new UntypedFormGroup({
    startDate: new UntypedFormControl(null, [Validators.required]),
    endDate: new UntypedFormControl(null, [Validators.required]),
    dateDropdown: new UntypedFormControl("Select Date Range", [Validators.required]),
    filterOperation: new UntypedFormControl("Select"),
    filterUser: new UntypedFormControl("Select"),
    filterOrg: new UntypedFormControl("Select")
  });
  private url: string;

  // idle timeout
  timedOut = false;
  countdown = 0;
  idleState = 'Not started.';
  lastPing?: Date = null;
  secondsUntilLogout = 30;
  idleConfirmationIsOpen = false;
  onIdleEndSubscription: Subscription;
  onTimeoutSubscription: Subscription;
  onIdleStartSubscription: Subscription;
  onTimeoutWarningSubscription: Subscription;

  constructor(
      private _service: CDSTreeService,
      private _oauth: OauthService,
      private _router: Router,
      private _orgservice: OrgService,
      private alertService: AlertService,
      private idle: Idle,
      private keepalive: Keepalive,
      private zone: NgZone,
      private confirmationService: ConfirmationService,
      private cd: ChangeDetectorRef,
      private _auditService: AuditService
  ) {
    // sets an idle timeout of 5 seconds, for testing purposes.
    this.idle.setIdle(5);
    // sets a timeout period of 5 seconds. after 900 seconds of inactivity (15 minutes), the user will be considered timed out.
    this.idle.setTimeout(895);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    // set the idle name for local storage
    this.idle.setIdleName('capdadmin');

    this.onIdleEndSubscription = this.idle.onIdleEnd.subscribe(() => {
      this.idleReset();
    });
    this.onTimeoutSubscription = this.idle.onTimeout.subscribe(() => {
      if (!window.location.href.includes('login')) {
        this.idleState = 'Timed out!';
        this.timedOut = true;
        this.closeIdleWarningDialog();
        this.alertService.showTraditionalAlert("You are being logged out due to inactivity.");
        this._oauth.doLogout();
        this.zone.run(() => {
          this._router.navigate(['login']);
          this.idleReset();
        });
      }
    });
    this.onIdleStartSubscription = this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'You\'ve gone idle!';
    });
    this.onTimeoutWarningSubscription = this.idle.onTimeoutWarning.subscribe((countdown) => {
      this.idleState = 'You will be logged out in ' + countdown + ' seconds.';
      $(".p-confirm-dialog-message").html(this.idleState);
      // Notify of logout 30 seconds before and don't alert if on the login screen
      if(countdown <= this.secondsUntilLogout && !window.location.href.includes('login') && !this.idleConfirmationIsOpen) {
        this.idle.clearInterrupts();
        this.idleConfirmationIsOpen = true;
        this.confirmationService.confirm({
          message: this.idleState,
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.closeIdleWarningDialog();
            this.idleReset();
          },
          reject: (type) => {
            this.closeIdleWarningDialog();
            this._oauth.doLogout();
            this.zone.run( () => {
              this._router.navigate(['login']);
              this.idleReset();
            });
          }
        });
      }
    });

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => {
      this.lastPing = new Date();
      this._oauth.refreshToken().then(token => {
      });
      $.getJSON(APIURL + 'actuator/info', function(data) {
        this.applicationVersion = data.build.version;
        document.getElementById('space-for-ver').innerText = "Current Admin Version: " + this.applicationVersion
      })
    });
  }

  idleReset(){
    this.countdown = null;
    this.lastPing = null;
    this.cd.detectChanges();
    this.idle.watch();
    this.idleState = "Started.";
    this.timedOut = false;
  }

  closeIdleWarningDialog() {
    this.confirmationService.close();
    this.idleConfirmationIsOpen = false;
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
  }

  logDatesAreEmpty() {
    if (!this.startDate || !this.endDate) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * Initialization of app. This largely handles routing to appropriate views:
   *
   * If the user is not logged in, the user is directed to the login page.
   * If the user is logged in as an M*Modal user, they are directed to the rules library.
   * If the user is logged in as a customer, they are directed to their organization's settings page.
   *
   * @async
   * @func ngOnInit
   */

  ngOnInit() {
    this.menuItems = [
      {
        label: 'About CAPD Admin',
        command: () => {
          this.onShowAboutModal();
        }
      },
      {
        label: 'FAQ Section',
        command: () => {
          this.onShowFAQ();
        }
      },
      {
        label: 'View Logs',
        command: () => {
          this.onViewLogs();
        }
      },
      {
        label: 'View Dashboard',
        command: () => {
          this.onViewClientDash();
        }
      },
      {
        label: 'Search All Settings',
        command: () => {
          this.onShowSearchAllSettingsDialog();
        }
      },
      {
        label: 'Find Org By OID',
        command: () => {
          this.onShowFindOrgByOidModal();
        }
      },
      {
        label: 'Sign out',
        command: () => {
          this.onSignOut();
        }
      }
    ];

    if (sessionStorage.getItem('uiVersion')) {
      this.uiVersion = sessionStorage.getItem('uiVersion');
    }

    this.maxDate = new Date(Date.now()).toISOString().split("T")[0];
    this.display = [];
    this.username = sessionStorage.getItem('username');

    // APP_INITIALIZER attempts session restoration via refresh cookie before ngOnInit runs.
    // If we still do not have an access token here, redirect to login.
    if (!SecureTokenService.getAccessToken()) {
      this._router.navigate(['login']);
      return;
    }

    this._service.getRootId().then(() => {

      // Routes to appropriate splash view.  The root ID of M*Modal is truncated to "~."
      // If the root isn't just "~," it's safe to assume they are a company.  Otherwise they're a root user.
      if (sessionStorage.getItem('rootId') !== '~' && sessionStorage.getItem('rootId') !== null && sessionStorage.getItem('uiVersion') == 'Classic') {
        this._router.navigate(['main/company/' + sessionStorage.getItem('rootId')]);
      } else if (sessionStorage.getItem('rootId') !== '~' && sessionStorage.getItem('rootId') !== null && sessionStorage.getItem('uiVersion') != 'Classic') {
          this._router.navigate(['main/company/' + sessionStorage.getItem('rootId') + '/settings/v2']);
      }

      // Once routed, the tree gets built.
      this._service.getTreeRoot().then((result: CDSNode[]) => {
        this.createTree(result);
      });

    })
        // if there's an error, the user has an invalid token and needs to log in again.
        .catch(() => {
          this._oauth.doLogout();
          this._router.navigate(['login']);
        });

    $.getJSON(APIURL + 'actuator/info', function(data) {
      this.applicationVersion = data.build.version;
      document.getElementById('space-for-ver').innerText = "Current Admin Version: " + this.applicationVersion
    })

    this.getEnvironment();

    const resizer = document.querySelector("#resizer");
    resizer.addEventListener("mousedown", (event) => {
      document.addEventListener("mousemove", this.resize, false);
      document.addEventListener("mouseup", () => {
        document.removeEventListener("mousemove", this.resize, false);
      }, false);
    });

    this.idleReset();

    this._oauth.getUIVersion().then((res: boolean) => {
      this.versionSwitchEnabled = res;
      if (res) {
        // reset to classic UI on refresh IF uiversion is null
        if (!sessionStorage.getItem('uiVersion')) {
          sessionStorage.setItem('uiVersion', 'Classic');
        }
        this.uiVersion = sessionStorage.getItem('uiVersion');
        this.uiChangeEvent.emit(this.uiVersion);
      } else {
        // if classic is not enabled, defualt to version 2
        this.uiVersion = 'Version 2';
        sessionStorage.setItem('uiVersion', 'Version 2');
        const currentUrl = window.location.href;

        if (!currentUrl.includes('v2')) {
          this.switchNavigation();
        }

        this.uiChangeEvent.emit(this.uiVersion);
      }
    });

  }

  ngOnDestroy() {
    if(this.onIdleEndSubscription) {
      this.onIdleEndSubscription.unsubscribe();
    }
    if(this.onTimeoutSubscription) {
      this.onTimeoutSubscription.unsubscribe();
    }
    if(this.onIdleStartSubscription) {
      this.onIdleStartSubscription.unsubscribe();
    }
    if(this.onTimeoutWarningSubscription) {
      this.onTimeoutWarningSubscription.unsubscribe();
    }
    this.idle.stop();
  }

  /**
   * This function initializes the tree by grabbing the root node.  The function here is a bit long-winded and a little deprecated,
   * but I'm going to keep it here because it still works.  Basically, it finds the top level node (expecting a long list of them)
   * and makes that the root.  Right now, the list it takes is one node long, but one can never be too careful.
   *
   * @func createTree
   * @param nodeList
   */
  createTree(nodeList: CDSNode[]) {

    let groupChildren = nodeList.filter(x => x.type == 'SettingsGroup')
    let nonGroupChildren = nodeList.filter(x => x.type != 'SettingsGroup')
    nodeList = [ ...groupChildren, ...nonGroupChildren]

    if(sessionStorage.getItem('rootId') == '~') {
      this.root = nodeList.filter(x => x.description == "M*Modal").shift();
      this.root.children = nodeList.filter(x => x.description != "M*Modal")
    } else {
      this._orgservice.getOrgInfo(sessionStorage.getItem('rootId')).then((r: CDSNode) => {
        this.root = r;
      })
    }

    /*
     * If a root is defined, the scope should be sent to either the type or the extensionId.
     * This scope prevents users with lower permissions from accessing higher-level locations by knowing the proper URL.
     */
    if (this.root){
      if (this.root.type) {
        sessionStorage.setItem('scope', this.root.type);
      } else {
        sessionStorage.setItem('scope', this.root.extensionId);
      }

      // This defines a node as "root" organizations, which load less initially to save space.
      this.root.distanceFromRoot = 0;
    }
  }

  /**
   * Sign-out method. Does a logout through the OAuth interface and navigates back to the login.
   *
   * @func onSignOut
   */
  onSignOut() {
    this._oauth.doLogout();
    this._router.navigate(['login']);
  }

  onViewClientDash() {
    // this._router.navigate(['clientdash']);

    this.clientDash.nativeElement.classList.toggle("show");
  }


  submitSearchAllSettings() {
    if(this.searchAllSettingsSearchText && this.searchAllSettingsSearchType) {
      this.searchAllSettings();
    }
  }

  clearSearchAllSettingsResults() {
    this.searchAllSettingsResult = [] as SearchAllSettingsDisplay[];
    this.invalidSearchAllSettingsResult = [] as SearchAllSettingsDisplay[];
  }
  /**
   * Display the Search All Settings window.
   *
   * @func onShowSearchAllSettingsDialog
   */
  onShowSearchAllSettingsDialog() {
    if (this.display.indexOf('searchAllSettings') < 0) {
      this.display.push('searchAllSettings');
    }
    this.resetSearchAllSettings();
    this.searchAllSettingsModal.nativeElement.classList.toggle("show");
  }
  /**
   * Perform the search of settings across all orgs.
   *
   * @func searchAllSettings
   */
  searchAllSettings() {
    let version = sessionStorage.getItem('uiVersion');
    this.loadingSearch = true;
    this.searchAllSettingsResult = [] as SearchAllSettingsDisplay[];
    this.invalidSearchAllSettingsResult = [] as SearchAllSettingsDisplay[];
    if (version == 'Classic') {
      this._orgservice.searchAllSettings(this.searchAllSettingsSearchType, this.searchAllSettingsSearchText.trim()).then(orgs => {
        orgs.forEach(org => {
          let settingDisplay = new SearchAllSettingsDisplay();
          settingDisplay.org = org["_id"];
          settingDisplay.orgName = org["orgName"]
          settingDisplay.key = this.searchAllSettingsSearchText;
          settingDisplay.value = org["settings"][this.searchAllSettingsSearchType][this.searchAllSettingsSearchText.trim()];
          if (settingDisplay.org.startsWith('~')) {
            this.searchAllSettingsResult.push(settingDisplay);
          } else {
            this.invalidSearchAllSettingsResult.push(settingDisplay);
          }
        });
      }).then(() => this.loadingSearch = false);
    } else {
      this._orgservice.searchAllSettingsV2(this.searchAllSettingsSearchType, this.searchAllSettingsSearchText.trim()).then(org => {
        org.forEach(org => {
          let settingDisplay = new SearchAllSettingsDisplay();
          settingDisplay.org = org["orgId"];
          settingDisplay.orgName = org["orgName"];
          settingDisplay.key = this.searchAllSettingsSearchText;
          settingDisplay.value = org["value"];
          if (settingDisplay.org.startsWith('~')) {
            this.searchAllSettingsResult.push(settingDisplay);
          } else {
            this.invalidSearchAllSettingsResult.push(settingDisplay);
          }
        });
      }).then(() => this.loadingSearch = false);
    }
  }

  exportSearchAllSettings() {
    const options = {
      filename: this.searchAllSettingsSearchType + '_settings_export_' + Date.now(),
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'settings_export',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };

    const csvExporter = new ExportToCsv(options);
    if(this.searchAllSettingsResult && this.searchAllSettingsResult.length > 0) {
      csvExporter.generateCsv(this.searchAllSettingsResult);
    } else {
      this.alertService.showAlert("No results to export.");
    }
  }

  /**
   * Resets the search all settings form.
   *
   * @func resetSearchAllSettings
   */
  resetSearchAllSettings() {
    this.searchAllSettingsSearchType = "";
    this.searchAllSettingsSearchText = "";
    this.searchAllSettingsResult = [] as SearchAllSettingsDisplay[];
    this.invalidSearchAllSettingsResult = [] as SearchAllSettingsDisplay[];
  }

  /**
   * Display the About modal window.
   *
   * @func onShowAboutModal
   */
  onShowAboutModal() {
    if (this.display.indexOf('about') < 0) {
      this.display.push('about');
    }

    this.aboutModal.nativeElement.classList.toggle("show");
  }

  onShowFAQ() {
    (<HTMLInputElement>document.getElementById('searchbar')).value = '';
    this.searchQuestion();
    this.hideTag();
    this.faq.nativeElement.classList.toggle("show");
  }
  hideFAQ() {
    this.faq.nativeElement.classList.toggle("show");
  }

  onViewLogs() {
    if (this.display.indexOf('auditLogs') < 0) {
      this.display.push('auditLogs');
    }

    if (this.uiVersion == 'Classic') {
      this.auditLogModal.nativeElement.classList.toggle("show");
    } else {
      this.auditLogv2Modal.nativeElement.classList.toggle("show");
    }
    this.focusAuditLogsbutton();
  }

  focusAuditLogsbutton() {
    setTimeout(() => {
      this.getAuditLogsButton.nativeElement.focus();
    });
  }

  /**
   * Remove the About modal window.
   *
   * @func onHideAboutModal
   */
  onHideAboutModal() {
    const idx = this.display.indexOf('about');
    if (idx >= 0) {
      this.display.splice(idx, 1);
    }

    this.aboutModal.nativeElement.classList.toggle("show");
  }
  /**
   * Remove the SearchAllSettings modal window.
   *
   * @func onHideSearchAllSettingsModal
   */
  onHideSearchAllSettingsModal() {
    const idx = this.display.indexOf('searchAllSettings');
    if (idx >= 0) {
      this.display.splice(idx, 1);
    }

    this.searchAllSettingsModal.nativeElement.classList.toggle("show");
  }

  onShowFindOrgByOidModal() {
    if (this.display.indexOf('findOrgByOidModal') < 0) {
      this.display.push('findOrgByOidModal');
    }

    this.findOrgByOidModal.nativeElement.classList.toggle("show");
  }

  onHideFindOrgByOidModal() {
    const idx = this.display.indexOf('findOrgByOidModal');
    if (idx >= 0) {
      this.display.splice(idx, 1);
    }

    this.findOrgByOidModal.nativeElement.classList.toggle("show");
    this.oidToFind = "";
    this.oidName = "";
    this.oidError = null;
  }

  onHideLogsModal() {
    const idx = this.display.indexOf('auditLogs');
    if(idx >= 0) {
      this.display.splice(idx, 1);
    }
    if (this.uiVersion == 'Classic') {
      this.auditLogModal.nativeElement.classList.toggle("show");
    } else {
      this.auditLogv2Modal.nativeElement.classList.toggle("show");
    }

    this.resetAuditLogSearch();
    this.clearForm();
  }

  resetAuditLogSearch() {
    this.showCustom = false;
    this.startDate = null;
    this.endDate = null;
    this.inputStartDate = null;
    this.inputEndDate = null;
    this.auditLogs = null;
    this.unfilteredLogs = null;
    this.displayFilters = false;
    this.loadingLogs = false;
    this.loadingSearch = false;
    this.pageNumber = 0;
  }

  onHideClientDash() {
    this.clientDash.nativeElement.classList.toggle("show");
  }

  alertUserAuditDates() {
    if (this.showCustom) {
      this.alertService.showAlert("Please enter valid dates using the start date and end date selectors.");
    } else {
      this.alertService.showAlert("Please select a date range from the dropdown.");
    }
  }

  async getAuditLogs(startDate, endDate, pageNumber) {
    this.loadingLogs = true;

    // Set the start date and time, catch if the input format is incorrect or un-serializable
    if(startDate !== null && startDate.length !== 0
        && endDate !== null && endDate.length !== 0) {

      if(startDate.length == 10) {
        startDate = startDate + 'T00:00:00'
      }

      if(endDate.length == 10) {
        endDate = endDate + 'T23:59:59'
      }

      try {
        this.startDate = new Date(startDate).toISOString();
        this.endDate = new Date(endDate).toISOString();
      } catch (Exception) {
        this.loadingLogs = false;
        this.startDate = null;
        this.endDate = null;
        this.alertUserAuditDates();
      }
    } else if(this.startDate == null && this.endDate == null) {
      this.loadingLogs = false;
      this.alertUserAuditDates();
    }

    // Set headers for GET request
    let myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Bearer " + SecureTokenService.getAccessToken());

    // Construct body for GET request
    let requestOptions: RequestInit = {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    };

    // Send start and end dates through URL parameters
    this.url = APIURL + 'api/audit_log/?startDate=' + this.startDate + '&endDate=' + this.endDate + '&skip=' + this.take * pageNumber + '&take=' + this.take;

    let auditLogs = fetch(this.url, requestOptions)
        .then(response => response.json())
        .then(result => {
          this.auditLogsRequested = true;
          // Notify the user if a null response is returned, otherwise display the data
          if (result !== null && result.length !== 0) {
            // Initialize audit logs and user list
            this.auditLogs = result;
            this.userList = [];
            this.orgList = [];

            if(this.auditLogs.length > 0) {
              for(let i = 0; i < this.auditLogs.length; i++) {
                // Create List of users
                this.userList.push(this.auditLogs[i].user);
                // Create List of orgs (only adding OID values)
                if(this.auditLogs[i].id !== null) {
                  if(this.auditLogs[i].id.substr(0, 2) == '~.') {
                    this.orgList.push(this.auditLogs[i].id);
                  }
                }
                // Ensure changerecord isn't null
                if(this.auditLogs[i].changeRecord !== null && this.auditLogs[i].changeRecord.snapshot !== null) {
                  let temp = this.auditLogs[i].changeRecord;
                  // Cut down the changerecord to only show the data snapshot as that's the most pertinent information
                  this.auditLogs[i].changeRecord = JSON.stringify(temp.snapshot, null, 4);
                } else {
                  // Set ChangeRecord to null for output purposes
                  this.auditLogs[i].changeRecord = 'Null';
                }
              }
              // Remove duplicates from Users list after all have been added
              this.userList = new Set(this.userList);
              this.orgList = new Set(this.orgList);
            }
            // Initialize unfiltered logs from response
            this.unfilteredLogs = this.auditLogs;
          } else {
            this.auditLogsRequested = true;
            // Notify of any errors with the request response
            this.auditLogs = [];
            this.unfilteredLogs = [];
          }
          this.loadingLogs = false;
          this.createCsv();
          return result;
        })
        .then(next => {
          //this.clearForm();
        }).then(() => {
          this.displayFilters = true;
        })
        .catch(error => {
          console.log('Audit Log Retrieval Failed.');
          console.log('error', error);
        });
    await auditLogs;
    // clear form values after we retrieve audits
  }

  // Clear the Create and Update forms so no data is mistakenly re-submitted
  private exportData: any[];
  clearForm() {
    this.getLogsForm.reset({
      dateDropdown: "Select Date Range",
      filterOperation: "Select",
      filterOperationType: "Select",
      filterUser: "Select",
      filterOrg: "Select"
    });

    this.getLogsFormV2.reset({
      dateDropdown: "Select Date Range",
      filterOperation: "Select",
    });
  }

  copyDate(date) {
    // Copy date value from record to clipboard
    var dummy = document.createElement("textarea");
    document.body.appendChild(dummy);
    dummy.value = new Date(date).toISOString();
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
  }

  onStartDateChange(newDate: string) {
    this.startDate = newDate;
  }

  onEndDateChange(newDate: string) {
    this.endDate = newDate;
  }

  setAuditDate(input) {
    this.startDate = null;
    this.endDate = null;
    this.pageNumber = 0;
    if(input == "Custom")
      this.showCustom = true;
    else {
      this.showCustom = false;
      // add 1 minute to current time to make sure any logs that logs occuring at current time are not excluded
      this.endDate = new Date(Date.now() + 60000).toISOString();
      if(input == "Last 24 Hours"){
        this.startDate = new Date(Date.now() - 86400000).toISOString()
      }
      else if(input == "Last 48 Hours") {
        this.startDate = new Date(Date.now() - 172800000).toISOString()
      }
      else if(input == "Last 1 Week") {
        this.startDate = new Date(Date.now() - 604800000).toISOString()
      }
    }
    this.focusAuditLogsbutton();
  }

  pullNextRecords() {
    this.pageNumber += 1;
    // Retrieve new audit logs
    this.getAuditLogs(this.startDate, this.endDate, this.pageNumber);
  }

  pullPreviousRecords() {
    this.pageNumber -= 1;
    // Retrieve new audit logs
    this.getAuditLogs(this.startDate, this.endDate, this.pageNumber);
  }

  getAuditLogsSubmit(startDate, endDate) {
    this.getAuditLogs(startDate, endDate, this.pageNumber);
  }

  async getAuditLogsVersion2(startDate, endDate) {
    this.loadingLogs = true;
    // Set the start date and time, catch if the input format is incorrect or un-serializable
    if (startDate !== null && startDate.length !== 0
        && endDate !== null && endDate.length !== 0) {

      if (startDate.length == 10) {
        startDate = startDate + 'T00:00:00';
      }

      if (endDate.length == 10) {
        endDate = endDate + 'T23:59:59';
      }

      try {
        this.startDate = new Date(startDate).toISOString();
        this.endDate = new Date(endDate).toISOString();
      } catch (Exception) {
        this.loadingLogs = false;
        this.startDate = null;
        this.endDate = null;
        this.alertUserAuditDates();
      }
    } else if (this.startDate == null && this.endDate == null) {
      this.loadingLogs = false;
      this.alertUserAuditDates();
    }

    let auditLogs = await this._auditService.getAuditLogsV2(this.startDate, this.endDate)
        .then((result: []) => {
          this.auditLogsRequested = true;
          // Notify the user if a null response is returned, otherwise display the data
          if (result !== null && result.length !== 0) {
            // Initialize audit logs and user list
            this.auditLogs = result;
            this.userList = [];
            this.orgList = [];

            if (this.auditLogs.length > 0) {
              for (let i = 0; i < this.auditLogs.length; i++) {
                // Create List of users
                this.userList.push(this.auditLogs[i].username);
                // Create List of orgs (only adding OID values)
                if (this.auditLogs[i].id !== null) {
                    this.orgList.push(this.auditLogs[i].orgOid);
                }
              }
              // Remove duplicates from Users list after all have been added
              this.userList = new Set(this.userList.filter(user => user !== null));
              this.orgList = new Set(this.orgList);
            }
            // Initialize unfiltered logs from response
            this.sortAuditLogs();
            this.unfilteredLogs = this.auditLogs;
          } else {
            this.auditLogsRequested = true;
            // Notify of any errors with the request response
            this.auditLogs = [];
            this.unfilteredLogs = [];
          }
          this.createCsvV2();
          return result;
        })
        .then(() => {
          this.displayFilters = true;
        })
        .catch(error => {
          console.log('Audit Log Retrieval Failed.');
          console.log('error', error);
          this.loadingLogs = false;
        });
    this.loadingLogs = false;
    // clear form values after we retrieve audits
  }

  submitAuditLogs() {
    this.getAuditLogs(this.getLogsForm.value.startDate, this.getLogsForm.value.endDate, this.pageNumber);
  }

  toggleSortType() {
    this.setSortType();
    this.sortAuditLogs();
  }

  setSortType() {
    if (this.sortType === 'sortTypeDesc') {
      this.sortType = 'sortTypeAsc';
    } else {
      this.sortType = 'sortTypeDesc';
    }
  }

  sortAuditLogs() {
    if (this.sortType === 'sortTypeDesc') {
      this.auditLogs.sort((a, b) => a.timeStamp > b.timeStamp ? -1 : 1)
    } else {
      this.auditLogs.sort((a, b) => a.timeStamp < b.timeStamp ? -1 : 1)
    }
  }

  /**
   * Creates a CSV file that shows the list of rules added to a customer in a portable table.
   *
   * @func createCsv
   */
  createCsv() {
    this.exportData = [];
    if(this.auditLogs.length > 0) {
      this.auditLogs.forEach(log => {
        const auditLogValue = {
          'Timestamp (UTC)': log.timestamp,
          'Operation': log.operation,
          'User': log.user,
          'State': log.state,
          'Value': log.changeRecord
        };
        this.exportData.push(auditLogValue);
      });
    }
  }

  /**
   * Creates a CSV file that shows the list of rules added to a customer in a portable table.
   *
   * @func createCsv
   */
  createCsvV2() {
    this.exportData = [];
    if(this.auditLogs.length > 0) {
      this.auditLogs.forEach(log => {
        const auditLogValue = {
          'Timestamp (UTC)': new Date(log.timeStamp).toUTCString(),
          'Org': log.orgOid,
          'Operation': log.operation,
          'User': log.username,
          'Value': log.change
        };
        this.exportData.push(auditLogValue);
      });
    }
  }

  /**
   * Saves a copy of the csv to your local drive.
   *
   * @func exportCsv
   */
  exportCsv() {
    let exportStartDate = this.auditLogs[(this.auditLogs.length - 1)].timestamp;
    let exportEndDate = this.auditLogs[0].timestamp;

    const options = {
      filename: 'AuditLogs' + exportStartDate + 'to' + exportEndDate,
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'Audit Logs From ' + exportStartDate + ' to ' + exportEndDate,
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };

    const csvExporter = new ExportToCsv(options);

    if (!(this.exportData == undefined) && this.auditLogs.length > 0) {
      csvExporter.generateCsv(this.exportData);
    } else {
      this.alertService.showAlert("Invalid Export. No data provided.");
    }
  }

  setOperationFilter(input) {
    this.currOperation = input;
    this.filterLogs();
  }

  setOperationTypeFilter(input) {
    this.searchOperationType = input;
    this.filterLogs();
  }

  setUserFilter(input) {
    this.searchUser = input;
    this.filterLogs();
  }

  setOrgFilter(input) {
    this.searchOrg = input;
    this.filterLogs();
  }

  filterLogs() {
    this.auditLogs = this.unfilteredLogs;
    this.searchForOperation(this.currOperation);
    this.searchForOperationType(this.searchOperationType);
    this.searchForUser(this.searchUser);
    this.searchForOrg(this.searchOrg);
  }

  searchForOperation(currOperation) {
    if(currOperation !== 'Select' && this.auditLogs.length > 0) {
      let filteredLogs = [];
      for(let i = 0; i < this.auditLogs.length; i++) {
        let auditLog = this.auditLogs[i];
        if(auditLog.operation.toLowerCase() == currOperation.toLowerCase()){
          filteredLogs.push(auditLog);
        }
      }
      // Display filtered logs
      this.auditLogs = filteredLogs;
    }
  }

  searchForOperationType(searchOperationType) {
    if(searchOperationType !== 'Select' && this.auditLogs.length > 0) {
      let filteredLogs = [];
      for(let i = 0; i < this.auditLogs.length; i++) {
        let auditLog = this.auditLogs[i];
        if(auditLog.type == searchOperationType.toLowerCase()){
          filteredLogs.push(auditLog);
        }
      }
      // Display filtered logs
      this.auditLogs = filteredLogs;
    }
  }


  searchForUser(searchUser) {
    if(searchUser !== 'Select' && this.auditLogs.length > 0) {
      let filteredLogs = [];
      for(let i = 0; i < this.auditLogs.length; i++) {
        let auditLog = this.auditLogs[i];
        if(auditLog.user == searchUser || auditLog.username == searchUser){
          filteredLogs.push(auditLog);
        }
      }
      // Display filtered logs
      this.auditLogs = filteredLogs;
    }
  }

  searchForOrg(searchOrg) {
    if(searchOrg !== 'Select' && this.auditLogs.length > 0) {
      let filteredLogs = [];
      for(let i = 0; i < this.auditLogs.length; i++) {
        let auditLog = this.auditLogs[i];
        if(auditLog.id == searchOrg || auditLog.orgOid == searchOrg){
          filteredLogs.push(auditLog);
        }
      }
      this.auditLogs = filteredLogs;
    }
  }

  hideTag() {
    const faqTag = document.querySelectorAll('.faq');
    faqTag.forEach(item =>{
      item.classList.remove('open')
      item.children[1].innerHTML = '&#43;'
    })
  }

  openTag() {
    const faqTag = document.querySelectorAll('.faq');
    faqTag.forEach(item => {
      item.addEventListener('click', e => {
        const input = e.target as HTMLElement;
        if (input.className == 'faq-btn-open') {
          this.hideTag();
          input.parentElement.classList.toggle('open');
          input.innerHTML = '&#8722;';
        }
      })
    })
  }

  searchQuestion() {
    this.noResults = false;
    let found = false;
    let input = (<HTMLInputElement>document.getElementById('searchbar')).value
    input=input.toLowerCase();
    const questions = document.querySelectorAll('.faq');
    for (let i = 0; i < questions.length; i++) {
      if (!questions[i].innerHTML.toLowerCase().includes(input)) {
        (questions[i] as HTMLElement).style.display="none";
      }
      else {
        (questions[i] as HTMLElement).style.display="list-item";
        found = true;
      }
    }
    if(!found) {
      this.noResults = true;
      this.alertService.showAlert("No result found.");
    }
  }

  getEnvironment() {
    this._service.getCAPDAdminEnvironmentName().then((environmentName: string): void => {
      if (environmentName) {
        this.environmentName = environmentName;
      } else {
        this.getEnvironmentLegacy();
      }
    });
  }

  getEnvironmentLegacy() {
    this._service.getCdsSystem().then((system: CDSSystem)=> {
      if (system) {
        if (system.cdsType == 'test')
          this.environmentName = 'Test'
        else if (system.cdsType == 'gi')
          this.environmentName = 'AWS GI'
        else if (system.cdsType == 'ct') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'AWS CT'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'AWS CT2 PIT'
        } else if (system.cdsType == 'pb') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'AWS CPM ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'AWS CPM2 PIT'
        } else if (system.cdsType == 'ukpl')
          this.environmentName = "AWS LCPP (London)"
        else if (system.cdsType == 'aupk')
          this.environmentName = 'AWS SCPP (Sydney)'
        else if (system.cdsType == 'capm')
          this.environmentName = 'AWS CCPP (Canada)'
        else if (system.cdsType == 'pa')
          this.environmentName = 'AWS PA (UCPP3)'
        else if (system.cdsType == 'production')
          this.environmentName = 'UAE Prod'
        else if (system.cdsType == 'depb')
          this.environmentName = 'AWS FCPP1 (Frankfurt)'
        else if (system.cdsType = 'prod') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'Prod ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'Prod PIT'
        } else if (system.cdsType = 'beta') {
          if (system.cdsName == 'fd-atl')
            this.environmentName = 'Beta ATL'
          else if (system.cdsName == 'fluencydirect')
            this.environmentName = 'Beta PIT'
        }
      }
    })
  }

  findOrgByOid() {
    this.oidError = false;
    this.loadingOidName = true
    this.oidName = ""

    // first let's check that org oid is formatted correctly
    // if not, we will append the needed details
    try {
      if (this.oidToFind.startsWith("2.16.840.1.113883.3.21")) {
        this.oidToFind = this.oidToFind.replace("2.16.840.1.113883.3.21", "~")
      } else if (this.oidToFind.startsWith(".")) {
        this.oidToFind = "~" + this.oidToFind
      } else if(!this.oidToFind.startsWith("~")) {
        this.loadingOidName = false
        this.oidError = true
        return;
      }
      if(this.oidToFind.includes("/")){
        this.oidToFind = this.oidToFind.replace("/", "%2B")
      }

      this._orgservice.getOrgInfo(this.oidToFind).then(res => {
        // if this call returns null, it may exist but not have children
        // check the tree to see if we already have this information
        if (res != null || res != undefined) {
          //@ts-ignore
          if (res.description && res.rootId.length == this.oidToFind.length) {
            //@ts-ignore
            this.oidName = res.description
          } else
            this.oidName = "No matching org found."
        }
        else {
          let found: CDSNode = this.root.children.find(x => (x.rootId == this.oidToFind) && (x.rootId.length == this.oidToFind.length))
          if(found)
            this.oidName = found.description
          else
            this.oidName = "No matching org found."
        }
      }).then(() => this.loadingOidName = false)
    } catch (e) {
      this.oidError = true
      this.loadingOidName = false
    }
  }

  collapseAudit(rowNum) {
    let elemDisplay = document.getElementById("audit-row-" + rowNum).style.display
    if(elemDisplay == "") {
      document.getElementById("audit-row-" + rowNum).style.display = "none"
      document.getElementById("audit-row-" + (rowNum+1)).style.display = "none"
      document.getElementById("collapse-icon-" + rowNum).classList.remove("ai-chevron-up")
      document.getElementById("collapse-icon-" + rowNum).classList.add("ai-chevron-down")
    }
    else {
      document.getElementById("audit-row-" + rowNum).style.display = ""
      document.getElementById("audit-row-" + (rowNum+1)).style.display = ""
      document.getElementById("collapse-icon-" + rowNum).classList.remove("ai-chevron-down")
      document.getElementById("collapse-icon-" + rowNum).classList.add("ai-chevron-up")
    }
  }

  collapseAuditV2(rowNum) {
    let elemDisplay = document.getElementById("audit-row-" + rowNum).style.display
    if(elemDisplay == "") {
      document.getElementById("audit-row-" + rowNum).style.display = "none"
      document.getElementById("collapse-icon-" + rowNum).classList.remove("ai-chevron-up")
      document.getElementById("collapse-icon-" + rowNum).classList.add("ai-chevron-down")
    }
    else {
      document.getElementById("audit-row-" + rowNum).style.display = ""
      document.getElementById("collapse-icon-" + rowNum).classList.remove("ai-chevron-down")
      document.getElementById("collapse-icon-" + rowNum).classList.add("ai-chevron-up")
    }
  }

  resize(e) {
    const size = `${e.x}px`;
    document.querySelector("#wrapper-nav-tree").style.width = size;
  }

  getAuditLogRowNumber(indexNumber) {
    return (indexNumber + 1) + (this.take * this.pageNumber)
  }

  changeUi() {
    this.uiVersion == 'Classic' ? this.uiVersion = 'Version 2' : this.uiVersion ='Classic'

    sessionStorage.setItem('uiVersion', this.uiVersion)

    this.uiChangeEvent.emit(this.uiVersion)

    this.switchNavigation()
  }

  switchNavigation() {
    var v2CompanyUrl = "";
    var companyUrl = "";

    if(this._router.url.includes("/content/v2") || this._router.url.includes("/grouprules/v2") || this._router.url.includes("/settings/v2")) {
      v2CompanyUrl = this._router.url;
      companyUrl = this._router.url.substring(0, v2CompanyUrl.length - 3)
    } else if(this._router.url.includes("/content")|| this._router.url.includes("/grouprules") || this._router.url.includes("/settings")) {
      v2CompanyUrl = this._router.url + "/v2"
      companyUrl = this._router.url
    }

    switch (this._router.url) {
        // if we are currently at the original library, switch to v2 library
      case "/main/mmodal/library":
        this._router.navigate(['main/mmodal/v2/library']);
        break;

        // if we are currently at the v2 library, switch to the oriiginal library
      case "/main/mmodal/v2/library":
        this._router.navigate(['main/mmodal/library']);
        break;

        // if we are currently at the original org level content tab, nav to v2
      case companyUrl:
        this._router.navigateByUrl(v2CompanyUrl)
        break;

        // if we are at the v2 org level content tab, nav to original
      case v2CompanyUrl:
        this._router.navigateByUrl(companyUrl)
        break;

      default:
        const currentUrl = this._router.url;
        this._router.navigateByUrl(currentUrl, { skipLocationChange: true })

    }
  }

  exportInvalidSearch() {
    const options = {
      filename: 'InvalidSettings_export_' + Date.now(),
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      showLabels: true,
      showTitle: true,
      title: 'InvalidSettings',
      useTextFile: false,
      useBom: true,
      useKeysAsHeaders: true
    };

    const csvExporter = new ExportToCsv(options);
    if (this.invalidSearchAllSettingsResult && this.invalidSearchAllSettingsResult.length > 0) {
      csvExporter.generateCsv(this.invalidSearchAllSettingsResult);
    } else {
      this.alertService.showAlert('No results to export.');
    }
  }
}
